import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button, Chip, Collapse, Stack, Typography } from "@mui/material";
import NextLink from "next/link";
import { useMemo } from "react";

import {
    ICustomerContextApplicationSummaryFragment,
    ICustomerContextCustomerFragment,
} from "../../contexts/customer/CustomerContext.generated";
import { useBoolean } from "../../hooks/useBoolean";
import { Routes } from "../../Routes";
import { ACTIVE_APPLICATION_STATUSES, isCustomerActive } from "../../utils/customerUtils";
import { ProductChip } from "../accounts/status/ProductChip";
import { ApplicationStatusChip } from "../kyc/ApplicationStatusChip";

export interface ICustomerSelectorProps {
    applications: ICustomerContextApplicationSummaryFragment[];
    customers: ICustomerContextCustomerFragment[];
    selectedCustomerId?: string;
    onCustomerSelect(id: string): void;
    isOmi: boolean;
}

export const CustomerSelector: React.FC<ICustomerSelectorProps> = ({
    applications,
    customers,
    selectedCustomerId,
    onCustomerSelect,
    isOmi,
}) => {
    const [open, , { toggle: toggleOpen }] = useBoolean(false);

    const { activeCustomers, activeApps, inactiveCustomers, inactiveApps } = useMemo(
        () => ({
            activeCustomers: customers?.filter((c) => isCustomerActive(c)) ?? [],
            inactiveCustomers: customers?.filter((c) => !isCustomerActive(c)) ?? [],
            activeApps: applications?.filter((a) => ACTIVE_APPLICATION_STATUSES.includes(a.status)) ?? [],
            inactiveApps: applications?.filter((a) => !ACTIVE_APPLICATION_STATUSES.includes(a.status)) ?? [],
        }),
        [applications, customers],
    );

    const hasInactiveAccounts = inactiveCustomers.length > 0 || inactiveApps.length > 0;

    return (
        <Stack spacing={4} p={4} sx={{ maxWidth: "sm", width: "100%" }}>
            <Stack spacing={1}>
                {activeCustomers.map(({ id, name, products }) => (
                    <Button
                        key={id}
                        variant="outlined"
                        onClick={() => onCustomerSelect(id)}
                        sx={{ padding: 3, justifyContent: "space-between" }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography>{name}</Typography>
                            {products.map((product) => (
                                <ProductChip key={product} product={product} />
                            ))}
                        </Stack>
                        {selectedCustomerId === id ? (
                            <Chip label="Selected" color="success" size="small" />
                        ) : (
                            <Typography>&#8594;</Typography>
                        )}
                    </Button>
                ))}
                {activeApps.map(({ id, dbaName, legalName, status }) => (
                    <NextLink
                        key={id}
                        href={Routes.KycApply.Application({ applicationId: id })}
                        passHref={true}
                        legacyBehavior={true}
                    >
                        <Button variant="outlined" sx={{ padding: 3, justifyContent: "space-between" }}>
                            <Stack direction="row" spacing={1} alignItems="baseline">
                                <Typography>{dbaName || legalName}</Typography>
                                <ApplicationStatusChip status={status} />
                            </Stack>
                            <Typography>&#8594;</Typography>
                        </Button>
                    </NextLink>
                ))}
            </Stack>
            {hasInactiveAccounts && (
                <Stack spacing={1}>
                    <Stack direction="row">
                        <Typography variant="body1">Inactive Accounts</Typography>
                        {open ? (
                            <ExpandLessIcon fontSize="small" onClick={toggleOpen} />
                        ) : (
                            <ExpandMoreIcon fontSize="small" onClick={toggleOpen} />
                        )}
                    </Stack>
                    <Collapse in={open} timeout="auto" unmountOnExit={true}>
                        <Stack spacing={1}>
                            {inactiveCustomers.map(({ id, name }) => (
                                <Button
                                    key={id}
                                    variant="outlined"
                                    onClick={() => onCustomerSelect(id)}
                                    sx={{ padding: 3, justifyContent: "space-between" }}
                                >
                                    <Typography>{name}</Typography>
                                </Button>
                            ))}
                            {inactiveApps.map(({ id, dbaName, legalName, status }) => (
                                <NextLink
                                    key={id}
                                    href={Routes.KycApply.Application({ applicationId: id })}
                                    passHref={true}
                                    legacyBehavior={true}
                                >
                                    <Button variant="outlined" sx={{ padding: 3, justifyContent: "space-between" }}>
                                        <Typography>
                                            {dbaName || legalName} <ApplicationStatusChip status={status} />
                                        </Typography>
                                    </Button>
                                </NextLink>
                            ))}
                        </Stack>
                    </Collapse>
                </Stack>
            )}
            {!isOmi && (
                <Stack justifyContent="space-between" alignItems="center" direction="row" spacing={1}>
                    <Typography color="gray">Interested in opening a Levro account for another business?</Typography>
                    <NextLink href={Routes.KycApply()} passHref={true} legacyBehavior={true}>
                        <Button variant="outlined" sx={{ whiteSpace: "nowrap" }}>
                            Apply Now &#8594;
                        </Button>
                    </NextLink>
                </Stack>
            )}
        </Stack>
    );
};
